import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { UtilsService } from '../../shared/utils.service';
import { EmployeesAddComponent } from '../employees-add/employees-add.component';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { saveAs } from 'file-saver';
import { RBACService } from '../../shared/rbac.service';
import { Router } from '@angular/router';
import { RbacRolesSelectorComponent } from '../../account/rbac-roles/rbac-roles-selector/rbac-roles-selector.component';

@Component({
  selector: 'app-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmployeesComponent implements OnInit, AfterViewInit {
  // RBAC permission flags, resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // RBAC roles feature flags (for the RBAC Roles column and mass assignment).
  canViewRbacRoles: boolean = false;
  canEditRbacRoles: boolean = false;

  // Plan feature plus RBAC flag for the "Send Message" menu item, resolved once in
  // ngOnInit() to avoid re-evaluating the checks on every change detection cycle.
  canComposeMessages: boolean = false;

  // Build displayedColumns dynamically, NOT as a static array.
  displayedColumns: string[] = [];

  listDataSource = new EmployeesDataSource(this.app, this.api);

  // The paginator and sorter for the list
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    public utils: UtilsService,
    private api: ApiRequestService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );
    this.canViewRbacRoles = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEditRbacRoles = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canComposeMessages =
      this.app.planCan(AppService.FEATURES.MESSAGES) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access employees.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [];
    if (this.canView || this.canDelete) {
      this.displayedColumns.push('select');
    }
    this.displayedColumns.push(
      'icons',
      'contact_person',
      'email',
      'mobile',
      'trade'
    );
    // Show the RBAC Roles column only when the user can view RBAC roles.
    if (this.canViewRbacRoles) {
      this.displayedColumns.push('rbac_roles');
    }
    this.displayedColumns.push('notes', 'date_created', 'actions');

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.listDataSource.getData();
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.listDataSource.limit = this.paginator.pageSize;
      this.listDataSource.offset = this.paginator.pageIndex;
      this.listDataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'date_created_UTC') {
        this.listDataSource.order_by = 'date_created';
      } else {
        this.listDataSource.order_by = this.sort.active;
      }

      this.listDataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onExport(type: string = 'csv', id?: number) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to export employees.'
      );
      return;
    }

    let ids: number[] = [];
    if (id) {
      ids.push(id);
    } else {
      ids.push(...this.listDataSource.selection.selected);
    }

    // Get the current date object
    const date = new Date();

    this.api
      .makeDownloadRequest(
        `v2/employees/export/${type}` +
          (ids.length > 0 ? '/' + ids.join(',') : ''),
        {},
        {
          order_by: this.listDataSource.order_by,
          order: this.listDataSource.order,
          search: this.listDataSource.search
        }
      )
      .then((file) => {
        saveAs(
          file,
          `${this.utils.getLangTerm('employees.plural', 'Employees')} - ${
            this.app.account.name
          } - ${date.getFullYear()}${date.getMonth() +
            1}${date.getDate()}.${type}`
        );
      });
  }

  onTrainingDocsExport(type: string = 'csv', id?: number) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to export employee training documents.'
      );
      return;
    }

    let ids: number[] = [];
    if (id) {
      ids.push(id);
    } else {
      ids.push(...this.listDataSource.selection.selected);
    }

    // Get the current date object
    const date = new Date();

    this.api
      .makeDownloadRequest(
        `v2/employees-folders/export/${type}` +
          (ids.length > 0 ? '/' + ids.join(',') : ''),
        {},
        {
          order_by: this.listDataSource.order_by,
          order: this.listDataSource.order,
          search: this.listDataSource.search,
          eager_load: true
        }
      )
      .then((file) => {
        saveAs(
          file,
          `${this.utils.getLangTerm(
            'employees.plural',
            'Employees'
          )} Training Documents - ${
            this.app.account.name
          } - ${date.getFullYear()}${date.getMonth() +
            1}${date.getDate()}.${type}`
        );
      });
  }

  /**
   * Adding a new employee.
   */
  onAddEmployee() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create employees.'
      );
      return;
    }

    this.utils
      .showComponentDialog(
        EmployeesAddComponent,
        {},
        {
          width: '900px'
        }
      )
      .then(() => {
        if (this.canView) {
          this.listDataSource.getData();
        }
      });
  }

  /**
   * Every partner-managed user id of the listing account (unfiltered by search and pagination).
   */
  get partnerManagedIds(): number[] {
    return this.listDataSource.lastResponse?.partnerManagedIds ?? [];
  }

  /**
   * Whether a role is a partner role (managed by the partner sync).
   *
   * @param role The role ({ id, name, partner_user_id }).
   */
  isPartnerRole(role: any): boolean {
    return role?.partner_user_id != null;
  }

  /**
   * Whether an employee row is a partner user that is read-only to the current user.
   *
   * @param element The employee row.
   */
  rowLocked(element: any): boolean {
    return this.rbac.partnerLockApplies(!!element?.is_partner_managed);
  }

  /**
   * The partner-managed user ids when the lock applies to the current user, else none.
   */
  lockedSelection(): number[] {
    return this.rbac.partnerLockApplies(true) ? this.partnerManagedIds : [];
  }

  /**
   * Whether any of the given user ids is a locked partner user.
   *
   * @param ids The user ids to check.
   */
  private hitsLockedSelection(ids: number[]): boolean {
    const locked: number[] = this.lockedSelection();
    return ids.some((id: number) => locked.includes(id));
  }

  /**
   * Delete the employee record.
   */
  onDelete(employeeId: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete employees.'
      );
      return;
    }

    // Partner users are managed by support.
    if (this.hitsLockedSelection([employeeId])) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }
    // Self-deletion guard: a user may not delete their own account.
    if (employeeId === this.app.user.id) {
      this.utils.showToast('You cannot delete your own account.');
      return;
    }

    this.utils.showModal(
      'Delete ' + this.utils.getLangTerm('employees.singular', 'Employee'),
      'Are you sure you want to delete this ' +
        this.utils.getLangTerm('employees.singular', 'Employee') +
        '?',
      () => {
        this.api
          .makeRequest('delete', `v2/employees/${employeeId}`)
          .then(() => {
            this.utils.showToast(
              'The ' +
                this.utils.getLangTerm('employees.singular', 'Employee') +
                ' was deleted.'
            );
            this.listDataSource.getData();
          })
          .catch((_error) => {
            this.utils.handleAPIErrors(_error);
          });
      }
    );
  }

  onDeleteSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete employees.'
      );
      return;
    }

    // Partner users are managed by support (the id list covers pages that were never loaded).
    if (this.hitsLockedSelection(this.listDataSource.selection.selected)) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    // Self-deletion guard: a user may not delete their own account, even within a bulk selection.
    if (this.listDataSource.selection.selected.indexOf(this.app.user.id) > -1) {
      this.utils.showToast(
        'You cannot delete your own account. Please deselect yourself and try again.'
      );
      return;
    }

    this.utils.showModal(
      'Delete Selected ' +
        this.utils.getLangTerm('employees.plural', 'Employees'),
      'Are you sure you want to delete the selected ' +
        this.utils.getLangTerm('employees.plural', 'Employees') +
        '?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v2/employees/` + this.listDataSource.selection.selected.join(',')
          )
          .then(() => {
            this.utils.showToast(
              'The selected ' +
                this.utils.getLangTerm('employees.plural', 'Employees') +
                ' was deleted.'
            );
            this.listDataSource.selection.clear();
            this.listDataSource.getData();
          })
          .catch((_error) => {
            this.utils.handleAPIErrors(_error);
          });
      }
    );
  }

  /**
   * Unlink a single RBAC role from an employee (chip "x").
   *
   * @param user_id The employee's user id.
   * @param role The RBAC role to unlink ({ id, name }).
   */
  onUnlinkRbacRole(user_id: number, role: any) {
    if (!this.canEditRbacRoles) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage RBAC roles.'
      );
      return;
    }

    // Partner users and partner roles are managed by support.
    if (this.hitsLockedSelection([user_id]) || this.rbac.partnerLockApplies(this.isPartnerRole(role))) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }
    this.utils.showModal(
      'Unlink RBAC Role',
      `Are you sure you want to unlink the "${
        role.name
      }" role from this ${this.utils.getLangTerm(
        'employees.singular',
        'Employee'
      )}?`,
      () => {
        this.api
          .makeRequest(
            'delete',
            `v2/employee/${user_id}/rbac-roles`,
            {},
            {
              rbac_role_ids: role.id
            }
          )
          .then(() => {
            this.utils.showToast('The RBAC role was unlinked.');
            this.listDataSource.getData();
          })
          .catch((_error) => {
            this.utils.handleAPIErrors(_error);
          });
      }
    );
  }

  /**
   * Mass-assign RBAC roles to the selected employees (additive).
   */
  onAssignRbacRolesToSelected() {
    if (!this.canEditRbacRoles) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage RBAC roles.'
      );
      return;
    }

    if (this.listDataSource.selection.selected.length == 0) {
      this.utils.showToast(
        'You need to select some ' +
          this.utils.getLangTerm('employees.plural', 'Employees') +
          ' first.'
      );
      return;
    }

    // Partner users' roles are managed by support. A mixed selection goes ahead: the API leaves the
    // partner users unchanged and names them in meta.skipped (api-spec 7.2a), shown in the toast below.
    // Only a selection of partner users alone is refused here.
    const locked: number[] = this.lockedSelection();
    if (this.listDataSource.selection.selected.every((id: number) => locked.includes(id))) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    // Capture the selected user ids before opening the dialog.
    let user_ids: number[] = [...this.listDataSource.selection.selected];
    let excluded_count: number = 0;

    // Support (super admin) users can only be managed from an internal account, so leave them out
    // of the bulk assignment otherwise. Only the bulk RBAC flow is affected: other bulk actions
    // keep the full selection. The API repeats this check and is the authoritative boundary.
    if (!this.app.account?.is_internal) {
      const support_user_ids: number[] = this.listDataSource.dataSubject.value
        .filter((employee: any) => employee.is_sitesoft_admin)
        .map((employee: any) => employee.id);
      user_ids = user_ids.filter((id: number) => !support_user_ids.includes(id));
      excluded_count = this.listDataSource.selection.selected.length - user_ids.length;

      if (user_ids.length == 0) {
        this.utils.showToast(
          'Support users cannot be assigned RBAC roles from this account. Select at least one other ' +
            this.utils.getLangTerm('employees.singular', 'Employee') +
            '.'
        );
        return;
      }
    }

    this.utils.showComponentDialog(
      RbacRolesSelectorComponent,
      {
        multiple: true
      },
      {
        width: '1200px'
      },
      (rbac_role_ids: number[]) => {
        if (typeof rbac_role_ids !== 'undefined' && rbac_role_ids.length > 0) {
          this.api
            .makeRequest('put', 'v2/employees/rbac-roles/assign', {
              user_ids: user_ids,
              rbac_role_ids: rbac_role_ids
            })
            .then((response: any) => {
              // Users removed client-side were never sent, so the API only reports the support users
              // the client missed (for example selected on another page). The two sets do not overlap.
              const skipped_count: number =
                excluded_count + (response?.meta?.skipped_user_ids?.length ?? 0);
              let message: string =
                'The selected RBAC role(s) were assigned to the selected ' +
                this.utils.getLangTerm('employees.plural', 'Employees') +
                '.';
              if (skipped_count > 0) {
                message +=
                  ' ' +
                  skipped_count +
                  ' support user(s) were excluded because they can only be managed by ' +
                  this.utils.getAppName() +
                  '.';
              }
              message += this.rbac.partnerSkippedNotice(response?.meta?.skipped);
              this.utils.showToast(message);
              this.listDataSource.selection.clear();
            })
            .catch((_error) => {
              this.utils.handleAPIErrors(_error);
            })
            .finally(() => {
              this.listDataSource.getData();
            });
        }
      }
    );
  }

  composeNewMessage() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to send messages.'
      );
      return;
    }

    if (this.listDataSource.selection.selected.length == 0) {
      this.utils.showToast(
        'You need to select some ' +
          this.utils.getLangTerm('employees.plural', 'Employees') +
          ' first.'
      );
      return; // early termination
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectUserIds: this.listDataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.listDataSource.getData();
      }
    );
  }

  // Expose RBACService and AppService to template for inline permission checks.
  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}

export class EmployeesDataSource extends ApiDataSource {
  order_by = 'contact_person';

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/employees', resetOffset);
  }
}
